<script setup lang="ts">
import { McDrawer, McButton, McIcon } from "@maersk-global/mds-components-core";
import { MdsConfig } from "@maersk-global/mds-config";
import useDownloadLists from "./composables/usedownloadlists";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useNotificationDrawerStore } from "@/store/NotificationDrawer/NotificationDrawer";
import Skeleton from "@/components/Skeleton/Skeleton.vue";
import { DownloadReportResponse, UploadReportResponse } from "@/global/interfaces";
import {
  isNotificationInProgress,
  isNotificationSuccessful,
  NotificationStatus,
  CUSTOMER_FEATURE_FLAG_NAME,
  DownloadActionTypes
} from "@/global";
import {
  asyncDownloadFileInBrowser,
  isAsyncReportingEnabled
} from "@/components/NotificationDrawer/utils/notificationDrawerUtils";
import { useUserStore } from "@/store";
import {
  ALL_NOTIFICATIONS_TAB,
  UPLOAD_NOTIFICATION_TABS,
  DOWNLOADS_NOTIFICATIONS_TAB,
  NotificationDrawerTabs,
  NotificationModuleIdentificationKeys
} from "./Constant";
import useUploadFile from "./composables/useUploadFile";

/**
 * NotificationDrawer component is used to display a list of notifications related to report downloads.
 * It includes a drawer that can be opened and closed, and displays notifications with their status.
 * Each notification includes an icon, file name, message, product name, creation date, and a tag.
 * This component is built column wise.
 * 1st column: Icon - (class - notification-column icon-column)
 * 2nd column: Content (file name, message, product name, creation date, and tag) - (class - notification-column content-column)
 * 3rd column: Status (with loading indicator for "In Progress" status and a download link if available) - (class - notification-column status-column)
 * useDownloadLists composable is used to fetch the notifications data.
 */

import { userAnalyticsService } from "@/services";

MdsConfig.iconsDynamicImportPath = "/node_modules/";
if (import.meta.env.PROD) {
  MdsConfig.iconsDynamicImportPath = "./";
}
const notificationDrawerStore = useNotificationDrawerStore();
const { isCustomerFeatureFlagEnabled } = useUserStore();

const isAsyncReportingEnabledFlag =
  isAsyncReportingEnabled() || isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.ENABLE_ASYNC_UPLOAD);
const ActiveTab = ref<NotificationDrawerTabs>(ALL_NOTIFICATIONS_TAB);

const isRtl = computed(() => locale.value === "ar");
const downloadList = useDownloadLists();
const uploadList = useUploadFile();
const notifications = computed(() => notificationDrawerStore.notificationsList);
const isNotificationsListInProgress = computed(() => notificationDrawerStore.getIsNotificationsListInProgress().value);

const { t, locale } = useI18n();

// Watch for locale changes and refresh notifications
watch(
  locale,
  () => {
    if (notifications.value.length > 0) {
      // Refresh localized content in composables
      downloadList.refreshLocalizedNotifications();
      uploadList.refreshLocalizedNotifications();

      // Update store with fresh localized data
      const downloadNotifications = Array.isArray(downloadList.notifications.value)
        ? downloadList.notifications.value
        : [];
      const uploadNotifications = Array.isArray(uploadList.notifications.value) ? uploadList.notifications.value : [];
      const combinedNotifications = [...downloadNotifications, ...uploadNotifications];
      notificationDrawerStore.setNotificationsList(combinedNotifications);
    }
  },
  { deep: true }
);

const isDownloadNotification = (n: any): n is DownloadReportResponse =>
  NotificationModuleIdentificationKeys.IS_DOWNLOAD_MODULE in n;
const isUploadNotification = (n: any): n is UploadReportResponse =>
  NotificationModuleIdentificationKeys.IS_UPLOAD_MODULE in n;

//  Centralized filter rules (extensible)
const filterMap: Record<NotificationDrawerTabs, (n: any) => boolean> = {
  [ALL_NOTIFICATIONS_TAB]: () => true,
  [DOWNLOADS_NOTIFICATIONS_TAB]: isDownloadNotification,
  [UPLOAD_NOTIFICATION_TABS]: isUploadNotification
};

// Helper function to translate tag content
const getTranslatedTag = (tag: any): string => {
  if (!tag) return "";
  if (tag.text) return tag.text; // Direct text, no translation needed
  if (tag.key && tag.params) return t(tag.key, tag.params);
  if (tag.key) return t(tag.key);
  return tag; // Fallback for old format
};

//  Filtered + transformed notifications
const displayNotifications = ref([]);

const updateDisplayNotifications = () => {
  displayNotifications.value = notifications.value.filter(filterMap[ActiveTab.value]).map((n) => ({
    ...n,
    displayProductName: n.productName ? t(n.productName.toUpperCase() + "_ID") : n.productName
  }));
};

watch([notifications, locale, ActiveTab], updateDisplayNotifications, { immediate: true, deep: true });

const visibleTabs = computed(() => {
  const tabs = [
    { key: ALL_NOTIFICATIONS_TAB, labelKey: "NOTIFICATION.DRAWER_TAB_HEADER.ALL" },
    { key: DOWNLOADS_NOTIFICATIONS_TAB, labelKey: "NOTIFICATION.DRAWER_TAB_HEADER.DOWNLOAD" },
    { key: UPLOAD_NOTIFICATION_TABS, labelKey: "NOTIFICATION.DRAWER_TAB_HEADER.UPLOAD" }
  ];

  return tabs.filter((tab) => {
    if (tab.key === DOWNLOADS_NOTIFICATIONS_TAB) return isAsyncReportingEnabled();
    if (tab.key === UPLOAD_NOTIFICATION_TABS)
      return isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.ENABLE_ASYNC_UPLOAD);
    return true;
  });
});

// Reactive tabs with translated labels
const translatedTabs = ref([]);

const updateTranslatedTabs = () => {
  translatedTabs.value = visibleTabs.value.map((tab) => ({
    ...tab,
    label: t(tab.labelKey)
  }));
};

watch([locale, visibleTabs], updateTranslatedTabs, { immediate: true, deep: true });

const handleTabChange = (tabKey: NotificationDrawerTabs) => {
  userAnalyticsService.trackEvent("Notification: Tab Changed", {
    page: "notification-drawer",
    tabName: tabKey
  });
  ActiveTab.value = tabKey;
};

const onClickCta = (parsedEvent: DownloadReportResponse | UploadReportResponse, action) => {
  const fileName = parsedEvent.file || "report.xlsx";
  userAnalyticsService.trackEvent("Notification: Action Clicked", {
    page: "notification-drawer",
    actionLabel: action.label,
    fileName,
    status: parsedEvent.status
  });

  switch (parsedEvent.status) {
    case NotificationStatus.SUCCESSFUL:
      asyncDownloadFileInBrowser(fileName, undefined, undefined, action.url);
      return;
    case NotificationStatus.FAILED: {
      // Check action type first, then notification type
      if (action.label === DownloadActionTypes.DELETE) {
        // Delete action - handle based on notification type
        if (isUploadNotification(parsedEvent)) {
          uploadList.deleteData(parsedEvent);
          return;
        }
        if (isDownloadNotification(parsedEvent)) {
          downloadList.deleteData(parsedEvent);
          return;
        }
      } else if (action.url) {
        // Download action with URL (error report or retry)
        asyncDownloadFileInBrowser(fileName, undefined, undefined, action.url);
        return;
      }

      return;
    }

    default:
      return;
  }
};

const onclose = () => {
  userAnalyticsService.trackEvent("Notification: Drawer Closed", {
    page: "notification-drawer"
  });
  notificationDrawerStore.setToggleDrawer(false);
};

// Track when drawer is opened
watch(
  () => notificationDrawerStore.toggleDrawer,
  (isOpen) => {
    if (isOpen) {
      userAnalyticsService.trackEvent("Notification: Drawer Opened", {
        page: "notification-drawer"
      });
    }
  }
);
</script>

<template>
  <mc-drawer
    v-if="isAsyncReportingEnabledFlag"
    data-test="notification-drawer"
    width="624px"
    :position="isRtl ? 'left' : 'right'"
    dimension="medium"
    :showclosebutton="false"
    fit="large"
    :open="notificationDrawerStore.toggleDrawer"
    :class="['notification-drawer', { 'rtl-drawer': isRtl, rtl: isRtl }]"
    @closed="onclose"
  >
    <span slot="heading" class="heading-with-close">
      <h4 class="heading" data-test="notification-drawer/heading">{{ t("NOTIFICATION.ACTIVITY_TRACKER") }}</h4>
      <mc-button
        icon="times"
        variant="plain"
        appearance="neutral"
        fit="small"
        class="close-button"
        data-test="notification-drawer/close-button"
        @click="onclose"
      />
    </span>
    <span v-if="isNotificationsListInProgress">
      <!-- TODO - calculate number of skeletons to show based on screen size -->
      <Skeleton
        v-for="n in 10"
        :key="n"
        data-test="notification-drawer/skeleton"
        :isLoading="notifications.length === 0"
        :text-lines="3"
        :textLineHeight="Array(3).fill('20px').concat(Array(3).fill('20px'))"
      />
    </span>
    <span
      v-else-if="notifications.length === 0"
      class="no-notifications"
      data-test="notification-drawer/no-notifications"
    >
      {{ t("NOTIFICATION.NO_NOTIFICATIONS") }}
    </span>

    <section v-else data-test="notification-drawer" :class="['downloads-section', { rtl: isRtl }]">
      <div class="tab-container" data-test="notification-drawer/tabs">
        <mc-tag
          v-for="tab in translatedTabs"
          :key="tab.key"
          :appearance="ActiveTab === tab.key ? 'info' : undefined"
          :title="`tabName:${tab.label}`"
          :data-test="`notification-drawer/tab-${tab.key}`"
          @click="handleTabChange(tab.key as NotificationDrawerTabs)"
        >
          {{ tab.label }}
        </mc-tag>
      </div>

      <ul class="notification-list" data-test="notification-drawer/notification-list">
        <li
          v-for="(notification, index) in displayNotifications"
          :key="index"
          :class="`notification-item ${notification.status}`"
          :data-test="`notification-drawer/notification-item-${notification.status}`"
        >
          <div class="notification-row">
            <div class="notification-column icon-column">
              <mc-icon :icon="notification.icon" size="16" />
            </div>

            <div class="notification-column content-column">
              <p class="notification-file" data-test="notification-drawer/file-info">
                <span class="file-name" data-test="notification-drawer/file-name">
                  {{ notification.file }}
                </span>
                <span
                  v-if="isNotificationSuccessful(notification) && 'fileSize' in notification && notification.fileSize"
                  data-test="notification-drawer/file-size"
                  >({{ notification.fileSize }})</span
                >
                <span v-if="isNotificationInProgress(notification)" class="separator">&middot;</span>
                <span v-for="(action, actionIndex) in notification?.action" :key="actionIndex">
                  <span
                    v-if="isNotificationInProgress(notification)"
                    :class="['actions', { delete: action.label === DownloadActionTypes.DELETE }]"
                    :data-test="`notification-drawer/action-${action.label.toLowerCase()}`"
                    @click="onClickCta(notification, action)"
                    >{{ t(`NOTIFICATION.${action?.label}`) }}</span
                  >
                  <span v-if="actionIndex !== notification.action.length - 1" class="separator">&middot;</span>
                </span>
              </p>
              <p class="notification-message" data-test="notification-drawer/message">{{ notification.message }}</p>

              <p class="notification-productName" data-test="notification-drawer/product-info">
                <b data-test="notification-drawer/product-name">{{ notification.displayProductName }}</b>
                <span class="separator">&middot;</span>
                <span class="notification-createdAt" data-test="notification-drawer/created-at">{{
                  notification.createdAt
                }}</span>
                <span class="separator">&middot;</span>
                <span class="tag" data-test="notification-drawer/tag">{{ getTranslatedTag(notification.tag) }}</span>
              </p>
            </div>

            <div class="notification-column status-column">
              <div class="status-container">
                <span
                  v-if="notification.status"
                  class="notification-status"
                  :class="notification.tagAppearance"
                  data-test="notification-drawer/status"
                >
                  <mc-loading-indicator
                    v-if="
                      notification.status === NotificationStatus.IN_PROGRESS ||
                      notification.status === NotificationStatus.QUEUED
                    "
                    Fit="small"
                    hiddenlabel
                  />
                  <mc-icon
                    v-if="
                      notification.status !== NotificationStatus.QUEUED &&
                      notification.status !== NotificationStatus.IN_PROGRESS
                    "
                    :icon="notification.statusIcon"
                    size="16"
                  />
                  {{ t(`NOTIFICATION.${notification.status}`) }}
                </span>
                <span
                  v-if="
                    (isDownloadNotification(notification) && !notification.isDownloadAcknowledged) ||
                    (isUploadNotification(notification) && !notification.isUploadAcknowledged)
                  "
                  class="blue-dot"
                  data-test="notification-drawer/unacknowledged-indicator"
                ></span>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </section>
  </mc-drawer>
</template>

<style scoped lang="scss">
@import "./NotificationDrawer.scss";

.rtl-drawer::part(dialog) {
  right: auto;
}
</style>
